import React from "react";
import {
    Activity,
    ShieldPlus,
    Waves,
    Moon,
    Bone,
    TrendingUp,
} from "lucide-react";
import {
    LOGO_URL,
    PHONE_DISPLAY,
    EMAIL,
    INSTAGRAM,
    waUrl,
} from "../lib/constants";

/**
 * 2-page A4 GYM brochure for The Reset Sanctuary.
 * Page 1: Problem · Solution · Benefits
 * Page 2: Science · Equipment · Tatva K · Packages · Contact
 * View at /brochure-gym. Print → Save as PDF.
 * Compliant: uses "Precision Frequency Recovery" / "Frequency-based sound"
 * (no "clinical" / "therapy" anywhere except Berklee credential).
 */

const GOLD = "#c9a227";
const GOLD_LINE = "rgba(201,162,39,0.25)";
const GOLD_LINE_SOFT = "rgba(201,162,39,0.15)";

const PageFrame = ({ children, testid }) => (
    <article
        className="brochure-page brochure-gym-page"
        data-testid={testid}
        style={{
            width: 794,
            minHeight: 1123,
            maxWidth: "100%",
            margin: "0 auto 32px",
            backgroundColor: "#0a0a0a",
            color: "#bbb",
            boxShadow:
                "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,162,39,0.18)",
            padding: "40px 44px 32px",
            boxSizing: "border-box",
            position: "relative",
            fontFamily: "Inter, sans-serif",
            display: "flex",
            flexDirection: "column",
        }}
    >
        <div
            style={{
                position: "absolute",
                inset: 18,
                border: "1px solid rgba(201,162,39,0.22)",
                pointerEvents: "none",
            }}
        />
        {children}
    </article>
);

const PageHeader = ({ kicker }) => (
    <header
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            marginBottom: 14,
        }}
    >
        <img
            src={LOGO_URL}
            alt="The Reset Sanctuary"
            style={{ height: 58 }}
        />
        <div style={{ textAlign: "right" }}>
            <p
                style={{
                    color: GOLD,
                    fontSize: 9,
                    letterSpacing: "0.35em",
                    textTransform: "uppercase",
                    margin: 0,
                    fontWeight: 500,
                }}
            >
                {kicker}
            </p>
            <p
                style={{
                    color: "#888",
                    fontSize: 8.5,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    margin: "4px 0 0",
                }}
            >
                Dubai · RAK
            </p>
        </div>
    </header>
);

const PageFooter = ({ pageNum, tagline }) => (
    <div
        style={{
            marginTop: "auto",
            paddingTop: 14,
            borderTop: `1px solid ${GOLD_LINE_SOFT}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            fontSize: 8,
            color: "#666",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            gap: 12,
        }}
    >
        <span>theresetsanctuary.org</span>
        <span style={{ color: GOLD, textAlign: "center", flex: 1 }}>
            {tagline}
        </span>
        <span>Page {pageNum} / 2</span>
    </div>
);

const SectionTitle = ({ kicker, title, color = "#fff" }) => (
    <div style={{ marginBottom: 10 }}>
        <p
            style={{
                color: GOLD,
                fontSize: 9,
                letterSpacing: "0.35em",
                textTransform: "uppercase",
                margin: "0 0 6px",
                fontWeight: 500,
            }}
        >
            {kicker}
        </p>
        <h2
            className="font-serif"
            style={{
                color,
                fontSize: 20,
                fontWeight: 500,
                margin: 0,
                lineHeight: 1.1,
                letterSpacing: "-0.005em",
            }}
        >
            {title}
        </h2>
    </div>
);

const BenefitTile = ({ Icon, label, body, testid }) => (
    <div
        data-testid={testid}
        style={{
            padding: "14px 13px",
            border: `1px solid ${GOLD_LINE}`,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            backgroundColor: "rgba(201,162,39,0.025)",
        }}
    >
        <Icon size={24} color={GOLD} strokeWidth={1.4} />
        <p
            style={{
                color: "#fff",
                fontSize: 10,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                margin: 0,
                fontWeight: 500,
                lineHeight: 1.25,
            }}
        >
            {label}
        </p>
        <p
            style={{
                color: "#aaa",
                fontSize: 8.5,
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 300,
            }}
        >
            {body}
        </p>
    </div>
);

const Pillar = ({ title, body }) => (
    <div
        style={{
            padding: "12px 12px",
            borderTop: `1px solid ${GOLD_LINE}`,
        }}
    >
        <p
            style={{
                color: GOLD,
                fontSize: 8.5,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                margin: "0 0 6px",
                fontWeight: 500,
                lineHeight: 1.3,
            }}
        >
            {title}
        </p>
        <p
            style={{
                color: "#bbb",
                fontSize: 9,
                lineHeight: 1.55,
                margin: 0,
                fontWeight: 300,
            }}
        >
            {body}
        </p>
    </div>
);

const Package = ({ tier, price, period, headline, items, featured }) => (
    <div
        style={{
            padding: "18px 16px 20px",
            border: featured ? `1px solid ${GOLD}` : `1px solid ${GOLD_LINE}`,
            backgroundColor: featured ? "rgba(201,162,39,0.04)" : "transparent",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            height: "100%",
        }}
        data-testid={`gym-package-${tier.toLowerCase()}`}
    >
        {featured && (
            <span
                style={{
                    position: "absolute",
                    top: -10,
                    left: 14,
                    backgroundColor: GOLD,
                    color: "#0a0a0a",
                    fontSize: 8,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    padding: "3px 10px",
                    fontWeight: 600,
                }}
            >
                Most Chosen
            </span>
        )}
        <p
            style={{
                color: GOLD,
                fontSize: 9,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                margin: 0,
                fontWeight: 500,
            }}
        >
            {tier}
        </p>
        <p
            className="font-serif"
            style={{
                color: "#fff",
                fontSize: 22,
                fontWeight: 400,
                margin: 0,
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
            }}
        >
            AED {price}
            <span
                style={{
                    fontSize: 11,
                    color: "#888",
                    fontFamily: "Inter, sans-serif",
                    letterSpacing: "0.05em",
                    marginLeft: 4,
                }}
            >
                /{period}
            </span>
        </p>
        <p
            style={{
                color: "#bbb",
                fontSize: 9.5,
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 300,
            }}
        >
            {headline}
        </p>
        <div
            style={{
                borderTop: `1px solid ${GOLD_LINE_SOFT}`,
                marginTop: 2,
                paddingTop: 8,
            }}
        >
            {items.map((it, i) => (
                <p
                    key={i}
                    style={{
                        color: "#fff",
                        fontSize: 9.5,
                        margin: "3px 0",
                        lineHeight: 1.4,
                    }}
                >
                    · {it}
                </p>
            ))}
        </div>
    </div>
);

const BrochureGym = () => {
    const gymWa = waUrl(
        "Hi, I'd like to book a gym recovery session at The Reset Sanctuary.",
    );

    return (
        <main
            data-testid="brochure-gym-page"
            style={{
                backgroundColor: "#0a0a0a",
                minHeight: "100vh",
                padding: "40px 16px 80px",
                color: "#bbb",
            }}
        >
            {/* Screen-only print button */}
            <div
                className="brochure-screen-only"
                style={{
                    maxWidth: 794,
                    margin: "0 auto 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                }}
            >
                <p
                    style={{
                        color: "#888",
                        fontSize: 12,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        margin: 0,
                    }}
                >
                    Gym Brochure — A4 · 2 pages
                </p>
                <button
                    onClick={() => window.print()}
                    className="btn-gold-solid"
                    data-testid="brochure-gym-print-btn"
                    style={{
                        cursor: "pointer",
                        fontFamily: "Inter, sans-serif",
                    }}
                >
                    Save as PDF / Print
                </button>
            </div>

            {/* ============== PAGE 1 ============== */}
            <PageFrame testid="brochure-gym-a4">
                <PageHeader kicker="Sound Recovery for Gyms" />

                {/* HERO */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                >
                    <p
                        style={{
                            color: GOLD,
                            fontSize: 9,
                            letterSpacing: "0.35em",
                            textTransform: "uppercase",
                            margin: "0 0 8px",
                            fontWeight: 500,
                        }}
                    >
                        Sound Recovery for Gym Performance
                    </p>
                    <h1
                        className="font-serif"
                        style={{
                            color: "#fff",
                            fontSize: 36,
                            fontWeight: 400,
                            lineHeight: 1.02,
                            letterSpacing: "-0.025em",
                            margin: "0 0 8px",
                            textTransform: "uppercase",
                        }}
                    >
                        Recover Harder.{" "}
                        <span style={{ color: GOLD }}>Grow Stronger.</span>
                    </h1>
                </section>

                {/* HERO IMAGE — male athlete */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        width: "100%",
                        aspectRatio: "16 / 8",
                        overflow: "hidden",
                        border: `1px solid ${GOLD_LINE}`,
                    }}
                    data-testid="gym-hero-image"
                >
                    <img
                        src="/brochure-assets/athlete-male.jpg"
                        alt="Athlete in gym recovery"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center 30%",
                            display: "block",
                        }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(180deg, rgba(10,10,10,0) 55%, rgba(10,10,10,0.65) 100%)",
                            pointerEvents: "none",
                        }}
                    />
                </section>

                {/* PROBLEM + SOLUTION */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                    }}
                >
                    <div data-testid="gym-problem">
                        <SectionTitle
                            kicker="The Problem"
                            title="Training at 100%. Recovering at 30%."
                        />
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.6,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            You train hard. But your body recovers at a
                            fraction of what it needs. Muscle soreness, poor
                            sleep, chronic stress and stalled plateaus are not
                            a willpower problem — they are a recovery deficit.
                        </p>
                    </div>
                    <div data-testid="gym-solution">
                        <SectionTitle
                            kicker="The Solution"
                            title="Precision Frequency Recovery."
                            color={GOLD}
                        />
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.6,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            45 minutes. Sound, Hz, vibration. A protocol
                            engineered to downshift the nervous system,
                            accelerate tissue repair, and unlock the deep
                            recovery your training already demands.
                        </p>
                    </div>
                </section>

                {/* 6 BENEFITS */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 12,
                    }}
                    data-testid="gym-benefits"
                >
                    <SectionTitle
                        kicker="The Benefits"
                        title="Six outcomes. One session."
                    />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 10,
                        }}
                    >
                        <BenefitTile
                            Icon={Activity}
                            label="Faster Muscle Recovery"
                            body="Frequency-based sound promotes circulation, reduces inflammation and speeds up muscle recovery."
                            testid="gym-benefit-recovery"
                        />
                        <BenefitTile
                            Icon={ShieldPlus}
                            label="Supports Injury Healing"
                            body="Helps strengthen tissue repair, reduces pain and supports long-term injury rehabilitation."
                            testid="gym-benefit-injury"
                        />
                        <BenefitTile
                            Icon={Waves}
                            label="Reduces Stress & Tension"
                            body="Lowers cortisol, relaxes the nervous system — a mental and physical reset."
                            testid="gym-benefit-stress"
                        />
                        <BenefitTile
                            Icon={Moon}
                            label="Improves Sleep Quality"
                            body="Deeper relaxation leads to better sleep, hormonal balance and overall recovery."
                            testid="gym-benefit-sleep"
                        />
                        <BenefitTile
                            Icon={Bone}
                            label="Relieves Joint & Tissue Tension"
                            body="Frequency sound waves help release tightness in joints, fascia and connective tissues."
                            testid="gym-benefit-joint"
                        />
                        <BenefitTile
                            Icon={TrendingUp}
                            label="Boosts Performance & Consistency"
                            body="Recover better, train harder, stay consistent with more energy, focus and resilience."
                            testid="gym-benefit-perf"
                        />
                    </div>
                </section>

                <PageFooter
                    pageNum={1}
                    tagline="Train Hard · Recover Deep · Become Unstoppable"
                />
            </PageFrame>

            {/* ============== PAGE 2 ============== */}
            <PageFrame testid="brochure-gym-a4-page2">
                <PageHeader kicker="Science · Equipment · Packages" />

                {/* HERO STRIP — female athlete */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        width: "100%",
                        aspectRatio: "16 / 7",
                        overflow: "hidden",
                        border: `1px solid ${GOLD_LINE}`,
                    }}
                    data-testid="gym-female-image"
                >
                    <img
                        src="/brochure-assets/athlete-female.jpg"
                        alt="Female athlete in recovery"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center 35%",
                            display: "block",
                        }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(180deg, rgba(10,10,10,0) 50%, rgba(10,10,10,0.65) 100%)",
                            pointerEvents: "none",
                        }}
                    />
                </section>

                {/* SCIENCE — 4 pillars */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                    data-testid="gym-science"
                >
                    <SectionTitle
                        kicker="The Science"
                        title="How the protocol works."
                    />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: "2px 16px",
                        }}
                    >
                        <Pillar
                            title="Theta Brainwaves"
                            body="The state where muscle repair accelerates and mental clarity returns."
                        />
                        <Pillar
                            title="Schumann Resonance · 7.83 Hz"
                            body="Earth's natural frequency — cellular recovery and nervous system regulation."
                        />
                        <Pillar
                            title="Binaural Beats"
                            body="Brainwave entrainment for deep relaxation, cognitive clarity and mental resilience."
                        />
                        <Pillar
                            title="Vibroacoustic Technology"
                            body="Low-frequency mechanical vibration reduces stress and supports nervous system regulation."
                        />
                    </div>
                </section>

                {/* EQUIPMENT GRID — 6 modalities, mini layout */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                    data-testid="gym-equipment"
                >
                    <SectionTitle
                        kicker="Equipment"
                        title="Engineered for measurable reset."
                    />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "2px 16px",
                        }}
                    >
                        <Pillar
                            title="PEMF Mat"
                            body="Pulsed electromagnetic stimulation for cellular recovery."
                        />
                        <Pillar
                            title="Frequency Generator"
                            body="Precision frequencies that restore balance and optimise vitality."
                        />
                        <Pillar
                            title="Vibroacoustic Bed"
                            body="Low-frequency mechanical vibration to biological tissue."
                        />
                        <Pillar
                            title="Binaural Beats"
                            body="Brainwave entrainment for deep relaxation."
                        />
                        <Pillar
                            title="Singing Bowls"
                            body="Harmonic resonance for autonomic downshift."
                        />
                        <Pillar
                            title="Tuning Forks · 432/528/174/741 Hz"
                            body="Targeted frequency points calibrated to the body's recovery bands."
                        />
                    </div>
                </section>

                {/* PRACTITIONER */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        padding: "12px 14px",
                        border: `1px solid ${GOLD_LINE}`,
                        display: "grid",
                        gridTemplateColumns: "62px 1fr",
                        gap: 12,
                        alignItems: "center",
                    }}
                >
                    <img
                        src="/brochure-assets/tatva.png"
                        alt="Tatva K"
                        style={{
                            width: 62,
                            height: 62,
                            objectFit: "cover",
                            objectPosition: "center 25%",
                            borderRadius: "50%",
                            border: `1px solid rgba(201,162,39,0.4)`,
                            display: "block",
                        }}
                        data-testid="gym-tatva-photo"
                    />
                    <div>
                        <p
                            style={{
                                color: GOLD,
                                fontSize: 9,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                margin: "0 0 4px",
                                fontWeight: 500,
                            }}
                        >
                            Led by Tatva K
                        </p>
                        <p
                            style={{
                                color: "#fff",
                                fontSize: 9,
                                lineHeight: 1.5,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            TEDx Speaker · 150M+ Streams · Reiki Grandmaster ·
                            Berklee College of Music — Music Therapy
                            Masterclass · ISTA Certified · IPHM Certified
                        </p>
                    </div>
                </section>

                {/* PACKAGES */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            marginBottom: 14,
                        }}
                    >
                        <h2
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 18,
                                fontWeight: 500,
                                margin: 0,
                                letterSpacing: "0.01em",
                            }}
                        >
                            Gym Packages
                        </h2>
                        <p
                            style={{
                                color: "#888",
                                fontSize: 9,
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                margin: 0,
                            }}
                        >
                            Monthly · Dubai & RAK
                        </p>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 14,
                        }}
                    >
                        <Package
                            tier="Basic"
                            price="3,999"
                            period="month"
                            headline="One reset a week. Stay consistent."
                            items={[
                                "1 session per week",
                                "Group format",
                                "45-min protocol",
                                "Post-session debrief",
                            ]}
                        />
                        <Package
                            tier="Standard"
                            price="7,999"
                            period="month"
                            headline="Two resets a week. Compound the gains."
                            featured
                            items={[
                                "2 sessions per week",
                                "Group format",
                                "45-min protocol",
                                "Recovery tracking",
                                "Priority scheduling",
                            ]}
                        />
                        <Package
                            tier="Premium"
                            price="11,999"
                            period="month"
                            headline="Three resets a week + 1-on-1."
                            items={[
                                "3 sessions per week",
                                "Group + 1-on-1 mix",
                                "60-min protocol",
                                "Personal protocol design",
                                "Direct line to practitioner",
                            ]}
                        />
                    </div>
                    <p
                        style={{
                            color: "#777",
                            fontSize: 8.5,
                            margin: "10px 0 0",
                            letterSpacing: "0.04em",
                        }}
                    >
                        All prices in AED, monthly. Cancel anytime. Custom
                        gym/franchise partnerships available.
                    </p>
                </section>

                {/* PAYMENT + CONTACT */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        display: "grid",
                        gridTemplateColumns: "1.1fr 1fr",
                        gap: 18,
                        marginBottom: 10,
                    }}
                >
                    <div>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 20,
                                fontWeight: 400,
                                margin: "0 0 6px",
                                letterSpacing: "-0.01em",
                            }}
                        >
                            Book Your{" "}
                            <span style={{ color: GOLD }}>Gym Recovery.</span>
                        </h2>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.55,
                                margin: 0,
                                maxWidth: 360,
                                fontWeight: 300,
                            }}
                        >
                            Reserve via WhatsApp. We confirm schedule, location
                            and tier within minutes.
                        </p>
                        <div
                            style={{
                                marginTop: 12,
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                            data-testid="gym-payments"
                        >
                            <p
                                style={{
                                    color: GOLD,
                                    fontSize: 8,
                                    letterSpacing: "0.3em",
                                    textTransform: "uppercase",
                                    margin: 0,
                                    fontWeight: 500,
                                }}
                            >
                                Flexible Payment Options
                            </p>
                            <div
                                style={{
                                    display: "flex",
                                    gap: 10,
                                    flexWrap: "wrap",
                                }}
                            >
                                <span
                                    style={{
                                        padding: "8px 14px",
                                        border: `1px solid ${GOLD_LINE}`,
                                        color: "#fff",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 10,
                                        backgroundColor:
                                            "rgba(255,255,255,0.02)",
                                    }}
                                    data-testid="gym-tabby"
                                >
                                    <img
                                        src="/brochure-assets/tabby-logo.png"
                                        alt="tabby"
                                        style={{
                                            height: 16,
                                            width: "auto",
                                            display: "block",
                                        }}
                                    />
                                    <span
                                        style={{
                                            color: "#bbb",
                                            fontSize: 8.5,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            borderLeft: `1px solid rgba(201,162,39,0.3)`,
                                            paddingLeft: 10,
                                        }}
                                    >
                                        4 interest-free payments
                                    </span>
                                </span>
                                <span
                                    style={{
                                        padding: "8px 14px",
                                        border: `1px solid ${GOLD_LINE}`,
                                        color: "#fff",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 10,
                                        backgroundColor:
                                            "rgba(255,255,255,0.02)",
                                    }}
                                    data-testid="gym-tamara"
                                >
                                    <img
                                        src="/brochure-assets/tamara-logo.png"
                                        alt="tamara"
                                        style={{
                                            height: 16,
                                            width: "auto",
                                            display: "block",
                                        }}
                                    />
                                    <span
                                        style={{
                                            color: "#bbb",
                                            fontSize: 8.5,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            borderLeft: `1px solid rgba(201,162,39,0.3)`,
                                            paddingLeft: 10,
                                        }}
                                    >
                                        Split your payment
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            textAlign: "right",
                            fontFamily: "Inter, sans-serif",
                        }}
                    >
                        <p
                            style={{
                                color: GOLD,
                                fontSize: 9,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                margin: "0 0 6px",
                                fontWeight: 500,
                            }}
                        >
                            Reserve via WhatsApp
                        </p>
                        <p
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 18,
                                fontWeight: 500,
                                margin: "0 0 6px",
                                letterSpacing: "0.02em",
                            }}
                        >
                            {PHONE_DISPLAY}
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 2px",
                            }}
                        >
                            {EMAIL}
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 2px",
                            }}
                        >
                            theresetsanctuary.org
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 10px",
                            }}
                        >
                            Instagram {INSTAGRAM}
                        </p>
                        <a
                            href={gymWa}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="gym-cta"
                            className="brochure-screen-only"
                            style={{
                                display: "inline-block",
                                color: "#0a0a0a",
                                backgroundColor: GOLD,
                                padding: "10px 18px",
                                fontSize: 11,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                fontWeight: 600,
                                textDecoration: "none",
                            }}
                        >
                            Book Your Gym Recovery
                        </a>
                    </div>
                </section>

                {/* DISCLAIMER */}
                <p
                    style={{
                        color: "#666",
                        fontSize: 7,
                        lineHeight: 1.5,
                        textAlign: "center",
                        margin: "0 0 10px",
                        position: "relative",
                        zIndex: 1,
                        paddingTop: 8,
                        borderTop: `1px solid ${GOLD_LINE_SOFT}`,
                    }}
                >
                    The Reset Sanctuary offers wellness and recovery sessions
                    through precision sound and frequency work. We do not
                    diagnose, treat, or cure medical conditions. Our services
                    are complementary wellness practices, not medical care.
                    Please consult a licensed healthcare provider for medical
                    concerns. © 2026 Tatva K.
                </p>

                <PageFooter
                    pageNum={2}
                    tagline="Heal Deeper · Move Better · Perform Stronger"
                />
            </PageFrame>
        </main>
    );
};

export default BrochureGym;
